import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { INVOICE_STATUS, fmtInvoiceDate } from "@/components/InvoiceRequestsTable";
import { Loader2, FileText } from "lucide-react";
import { toast } from "sonner";

export default function AdminInvoices() {
  const [items, setItems] = useState(null);
  const [numbers, setNumbers] = useState({});
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/admin/invoices").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const update = async (inv, status) => {
    setBusy(inv.id + status);
    try {
      await api.patch(`/admin/invoices/${inv.id}`, { status, invoice_number: numbers[inv.id] ?? inv.invoice_number ?? null });
      toast.success(status === "paid" ? "Marked paid — access unlocked" : `Marked ${INVOICE_STATUS[status].label.toLowerCase()}`);
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Update failed"); } finally { setBusy(null); }
  };

  if (!items) return <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />;
  return (
    <div className="space-y-3" data-testid="admin-invoices">
      <p className="text-sm text-muted-foreground">Customers who asked to pay by bank transfer. Issue the invoice from your accounts software, record its number, then mark <strong>Paid</strong> when the money lands — their plan or product unlocks automatically.</p>
      {!items.length && <p className="text-sm text-muted-foreground py-8 text-center" data-testid="admin-invoices-empty">No invoice requests yet.</p>}
      {items.map((inv) => {
        const st = INVOICE_STATUS[inv.status] || INVOICE_STATUS.requested;
        const open = inv.status === "requested" || inv.status === "invoiced";
        return (
          <div key={inv.id} className="rounded-2xl border border-border bg-card p-4 flex flex-col lg:flex-row lg:items-center gap-3" data-testid={`admin-invoice-${inv.id}`}>
            <div className="flex-1 min-w-0 text-sm">
              <p className="font-semibold inline-flex items-center gap-2"><FileText className="w-4 h-4 text-primary" /> {inv.org_name} · £{Number(inv.amount_gbp).toLocaleString("en-GB")} <Badge className={st.cls}>{st.label}</Badge></p>
              <p className="text-muted-foreground">{inv.title} · {inv.user_name || inv.user_email} ({inv.user_email}) · {fmtInvoiceDate(inv.created_at)}</p>
              <p className="text-xs text-muted-foreground mt-1 whitespace-pre-line">{inv.billing_address}{inv.po_number ? ` · PO ${inv.po_number}` : ""}{inv.notes ? ` · ${inv.notes}` : ""}</p>
            </div>
            {open && (
              <div className="flex items-center gap-2 flex-wrap">
                <Input placeholder="Invoice no." className="w-32 h-9" defaultValue={inv.invoice_number || ""} onChange={(e) => setNumbers((n) => ({ ...n, [inv.id]: e.target.value }))} data-testid={`admin-invoice-number-${inv.id}`} />
                {inv.status === "requested" && <Button size="sm" variant="outline" disabled={!!busy} onClick={() => update(inv, "invoiced")} data-testid={`admin-invoice-sent-${inv.id}`}>Invoice sent</Button>}
                <Button size="sm" disabled={!!busy} onClick={() => update(inv, "paid")} data-testid={`admin-invoice-paid-${inv.id}`}>{busy === inv.id + "paid" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Mark paid"}</Button>
                <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => update(inv, "cancelled")} data-testid={`admin-invoice-cancel-${inv.id}`}>Cancel</Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
